/**
 * SummaryCommunication.jsx
 * Exact replica of Excel "Summary_Communication" sheet — all 44 columns
 *
 * CN CODE FEATURE (added):
 *   - Col 3 (CN Codes) now uses <CnCodeAutocomplete /> (filtered by col 2)
 *   - Col 4 (CN Name) is auto-filled (read-only) when a CN code is selected
 *   - Manual cell state lives in shared ProductManualContext, so a CN code
 *     typed in Summary_Products appears here too (and vice-versa).
 */

import React, { useMemo, useState } from "react";
import { useFormContext } from "../context/FormContext";
import { useProductManual } from "../context/ProductManualContext";
import { D as DFormulas } from "../utils/CbamFormulas";
import exportSummaryExcel from "../utils/exportSummaryExcel";
import { buildPrecursorData } from "../utils/exportSummaryExcel";
import { getDescriptionForCompactCode, getDisplayCodeForCompact } from "../utils/cnCodeLookup";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const dash = (v) => (v == null || v === "" ? "—" : String(v));
const fmtN = (v, d = 6) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0
    ? n.toLocaleString("en-US", { maximumFractionDigits: d })
    : "—";
};
const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("en-GB");
  } catch {
    return String(v);
  }
};

// ─── 44 column definitions ────────────────────────────────────────────────────
// type: "auto" = green (read from forms), "manual" = yellow (user types in UI)
const COLS = [
  {
    n: 1,
    label: "Production process from which the products arise",
    w: 100,
    type: "auto",
  },
  { n: 2, label: "Type of aggregated good or precursor", w: 160, type: "auto" },
  { n: 3, label: "CN Codes", w: 160, type: "auto" },
  { n: 4, label: "CN Name", w: 220, type: "auto" },
  {
    n: 5,
    label:
      "Product name\n(used for communication with reporting declarant, e.g. on invoices)",
    w: 160,
    type: "manual",
  },
  { n: 6, label: "SEE (direct)\ntCO2e/t", w: 90, type: "auto" },
  { n: 7, label: "SEE (indirect)\ntCO2e/t", w: 90, type: "auto" },
  { n: 8, label: "SEE (total)\ntCO2e/t", w: 90, type: "auto" },
  { n: 9, label: "Unit", w: 45, type: "auto" },
  { n: 10, label: "Share of emissions by default value", w: 120, type: "auto" },
  { n: 11, label: "Source for electricity EF", w: 120, type: "auto" },
  { n: 12, label: "Embedded electricity (MWh/t)", w: 110, type: "auto" },
  {
    n: 13,
    label: "The main reducing agent of the precursor, if known",
    w: 145,
    type: "manual",
  },
  { n: 14, label: "Steel mill identification number", w: 120, type: "manual" },
  { n: 15, label: "% Mn", w: 55, type: "manual" },
  { n: 16, label: "% Cr", w: 55, type: "manual" },
  { n: 17, label: "% Ni", w: 55, type: "manual" },
  { n: 18, label: "% other alloys", w: 80, type: "manual" },
  { n: 19, label: "% carbon", w: 65, type: "manual" },
  { n: 20, label: "t scrap per t steel", w: 90, type: "manual" },
  { n: 21, label: "% other materials", w: 90, type: "manual" },
  { n: 22, label: "% pre-consumer scrap", w: 90, type: "manual" },
  { n: 23, label: "t scrap per t aluminium", w: 100, type: "manual" },
  { n: 24, label: "% non-aluminium elements", w: 100, type: "manual" },
  { n: 25, label: "Clinker factor", w: 80, type: "manual" },
  { n: 26, label: "Calcined or not", w: 80, type: "manual" },
  {
    n: 27,
    label: "Concentration, if hydrous solution",
    w: 110,
    type: "manual",
  },
  { n: 28, label: "% nitric acid", w: 75, type: "manual" },
  { n: 29, label: "% urea", w: 65, type: "manual" },
  { n: 30, label: "% N contained", w: 80, type: "manual" },
  { n: 31, label: "% N as ammonium (NH4+)", w: 110, type: "manual" },
  { n: 32, label: "% N as nitrate (NO3–)", w: 105, type: "manual" },
  { n: 33, label: "% N as Urea", w: 75, type: "manual" },
  { n: 34, label: "% N in other (organic) forms", w: 110, type: "manual" },
  { n: 35, label: "Type of instrument (carbon pricing)", w: 130, type: "auto" },
  {
    n: 36,
    label: "Share of total embedded emissions covered by the carbon price",
    w: 150,
    type: "auto",
  },
  {
    n: 37,
    label: "Embedded emissions covered by the carbon price",
    w: 140,
    type: "auto",
  },
  { n: 38, label: "Currency", w: 65, type: "auto" },
  {
    n: 39,
    label: "Carbon price (CP) due\n(per produced t or MWh)",
    w: 110,
    type: "auto",
  },
  { n: 40, label: "Type of rebate", w: 90, type: "manual" },
  {
    n: 41,
    label: "Share of embedded emissions covered by the rebate",
    w: 145,
    type: "manual",
  },
  {
    n: 42,
    label: "Embedded emissions covered by rebate",
    w: 130,
    type: "manual",
  },
  {
    n: 43,
    label: "Amount of rebate\n(per produced t or MWh)",
    w: 110,
    type: "auto",
  },
  {
    n: 44,
    label: "Result: Effective CP due\n(per produced t or MWh)",
    w: 130,
    type: "auto",
  },
];

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  .sc { font-family: Calibri, Arial, sans-serif; font-size: 10.5px; color: #000; background: #f5f5f5; min-height: 100vh; padding-bottom: 40px; }
  .sc-top { background: #376366; color: #fff; padding: 7px 14px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .sc-top-title { font-size: 13px; font-weight: 700; flex: 1; }
  .sc-top-tag { background: rgba(255,255,255,.15); border: 1px solid rgba(255,255,255,.3); padding: 2px 9px; font-size: 10px; border-radius: 2px; }
  .sc-info { background: #E8F4F8; border-left: 4px solid #376366; padding: 6px 14px; font-size: 10px; color: #444; }
  .sc-sec { margin: 12px 10px 0; }
  .sc-band { background: #376366; color: #fff; font-weight: 700; font-size: 11.5px; padding: 5px 10px; }
  .sc-scroll { overflow-x: auto; background: #fff; }
  .sc-tbl { border-collapse: collapse; font-size: 10.5px; white-space: nowrap; }
  .sc-tbl th, .sc-tbl td { border: 1px solid #c0c0c0; padding: 2px 5px; vertical-align: middle; min-height: 20px; }
  .ch  { background: #376366; color: #fff; font-weight: 700; text-align: center; vertical-align: bottom; }
  .csh { background: #4d8285; color: #fff; font-weight: 700; padding: 4px 6px; }
  .clb { background: #f2f2f2; color: #333; }
  .cgn { background: #CCFFCC; }
  .cyw { background: #FFFF99; }
  .ctot { background: #D9E1F2; font-weight: 700; }
  .cid  { background: #E5F0F1; color: #254244; font-weight: 700; text-align: center; }
  .cwh  { background: #fff; }
  .rnum { text-align: right; }
  .rct  { text-align: center; }
  .sc-input { width: 100%; border: none; background: transparent; font-size: 10.5px; font-family: inherit; padding: 0; outline: none; color: #000; }
  .cyw .sc-input { background: #FFFF99; }
  /* CN code autocomplete cell */
  .cn-cell { padding: 0 2px !important; background: #FFFF99; }
  .cn-cell .MuiAutocomplete-root { background: transparent; }
  .cn-cell .MuiInputBase-root { padding: 0 !important; min-height: 22px; }
  .cn-cell input { font-size: 10.5px !important; }
  /* CN name read-only — wrap text */
  .cnname-cell { white-space: normal; word-break: break-word; font-size: 10px; line-height: 1.25; padding: 3px 5px; }
  .sc-legend { display: flex; gap: 16px; flex-wrap: wrap; padding: 5px 10px; font-size: 10px; color: #555; background: #fafafa; border: 1px solid #ddd; margin-top: 4px; }
  .sc-legend span { display: flex; align-items: center; gap: 4px; }
  .sc-legend b { display: inline-block; width: 13px; height: 13px; border: 1px solid #bbb; }
  .sc-divider-row td { background: #4d8285; color: #fff; font-weight: 700; font-size: 10px; padding: 3px 6px; }
`;

// ─── Main component ───────────────────────────────────────────────────────────
const SummaryCommunication = ({ readOnly = false }) => {
  const { allFormData } = useFormContext();

  //  NEW: shared manual cell state
  const { getRow, setCell, setCells, data: manualData } = useProductManual();

  const fA = allFormData?.formA || {};
  const fC = allFormData?.formC || {};
  const fD = allFormData?.formD || {};
  const fE = allFormData?.formE || {};
  const fF = allFormData?.formF || {};

  const aggGoods   = fA.aggregatedGoods    || [];
  const procs      = fA.relevantProcesses  || [];
  const purchasedPrecursors = fA.purchasedPrecursors || [];
  const goodsLookup = Object.fromEntries(
  aggGoods.map(g => [g.category, g])
);

  // CN code now comes from the precursor linked to this process.
  // Prefer explicit Form A links, but fall back to Form E precursors
  // auto-matched by goods category / process if Form A row has no link.
  const getCnCodeForProcess = (processId) => {
    const linkedA = purchasedPrecursors.find(
      (pp) => pp.linkedProcessId === processId
    );
    if (linkedA?.cnCode) return linkedA.cnCode;

    const linkedE = formEPrecursors.find(
      (ep) => ep.linkedProcessId === processId
    );
    return linkedE?.emissions?.cnCode || "";
  };

  const calcBased     = Number(fC?.ghgBalanceByMethod?.calculationBased) || 0;
  const pfcTotal      = Number(fC?.ghgBalanceByMethod?.totalPFC)         || 0;
  const measBased     = Number(fC?.ghgBalanceByMethod?.measurementBased) || 0;
  const otherEm       = Number(fC?.ghgBalanceByMethod?.other)            || 0;
  const totalDirect   = Number(fC?.directEmissions)   || Number(fC?.totalDirectEmissions)   || 0;
  const totalIndirect = Number(fC?.indirectEmissions) || Number(fC?.totalIndirectEmissions) || 0;
  const totalAll      = totalDirect + totalIndirect;
  const dqGeneral     = fC?.dataQuality?.generalInfo   || "—";
  const dqJustif      = fC?.dataQuality?.justification || "—";
  const dqAssur       = fC?.dataQuality?.assurance      || "—";

  const dProcesses = fD?.processes || [];
  const formEPrecursors = fE?.precursors || [];

  const makeMatchesProc = (proc) => {
    const procId = proc.processId || proc.id;
    const procName = proc.title || proc.name || "";
    return (cp) => {
      if (!cp.processId && !cp.process) return false;
      return (
        cp.processId === procId ||
        cp.processId === proc.id ||
        cp.processId === proc.processId ||
        cp.process === procName ||
        cp.processId === procName
      );
    };
  };

  const computeDefaultEmFromUpstream = (upstreamProc, qty) => {
    if (!upstreamProc || !qty) return 0;
    const matches = makeMatchesProc(upstreamProc);
    const upstreamProd = (upstreamProc.productionLevels || []).reduce(
      (s, r) => s + Number(r.amounts || 0),
      0,
    );
    if (upstreamProd <= 0) return 0;
    return formEPrecursors.reduce((sum, prec) => {
      if (prec.emissions?.sourceSeeDirect !== "Default") return sum;
      const consumed = (prec.consumedInProcesses || []).find(matches);
      if (!consumed || !Number(consumed.amounts)) return sum;
      const seeDirect = Number(prec.emissions?.seeDirect || 0);
      const fractionInUpstream = Number(consumed.amounts) / upstreamProd;
      return sum + seeDirect * fractionInUpstream * qty;
    }, 0);
  };

  const processRows = useMemo(
    () =>
      dProcesses.map((proc, idx) => {
        const aProc = procs[idx] || {};
        const id = proc.id || proc.processId || aProc.id || `P${idx + 1}`;
        const name = proc.name || proc.title || aProc.name || `P${idx + 1}`;
        const cat =
          aProc.aggregatedGoodsCategory || proc.aggregatedGoodsCategory || "—";
        const see = DFormulas.calculateSEE(
          { ...proc, id, name, aggregatedGoodsCategory: cat },
          formEPrecursors,
        );
        const totProd = see.productionLevelsTotalAuto || 0;
        const elecMWh = Number(proc.electricityConsumption?.consumption || 0);
        const procElecPerT = totProd > 0 ? elecMWh / totProd : 0;

        const matchesProc = makeMatchesProc(proc);
        const directPrecElecSum = formEPrecursors.reduce((sum, prec) => {
          const consumed = (prec.consumedInProcesses || []).find(matchesProc);
          if (!consumed || !Number(consumed.amounts)) return sum;
          const massPerT = totProd > 0 ? Number(consumed.amounts) / totProd : 0;
          return sum + massPerT * Number(prec.emissions?.secIndirect || 0);
        }, 0);

        const procIdx = dProcesses.findIndex(
          (p) => (p.processId || p.id) === (proc.processId || proc.id),
        );
        const consumedProcSum = (proc.consumedOtherProcesses || []).reduce(
          (acc, cp2) => {
            if (!cp2.processId) return acc;
            if (cp2.processId === (proc.processId || proc.id)) return acc;
            const otherProc = dProcesses.find(
              (p) => (p.processId || p.id) === cp2.processId,
            );
            if (!otherProc) return acc;
            const otherIdx = dProcesses.findIndex(
              (p) => (p.processId || p.id) === cp2.processId,
            );
            if (procIdx <= otherIdx) return acc;
            const otherSee = DFormulas.calculateSEE(otherProc, formEPrecursors);
            const otherProd = otherSee.productionLevelsTotalAuto || 0;
            const explicitQty = Number(cp2.amounts);
            const derivedQty =
              explicitQty > 0
                ? explicitQty
                : otherProd -
                  Number(
                    otherProc.productionDetails?.amountsProducedForMarket || 0,
                  );
            const qty = derivedQty > 0 ? derivedQty : 0;
            if (!qty) return acc;
            const massPerT = totProd > 0 ? qty / totProd : 0;
            const otherElec = Number(
              otherProc.electricityConsumption?.consumption || 0,
            );
            const otherProcElecT = otherProd > 0 ? otherElec / otherProd : 0;
            const otherMatchesProc = makeMatchesProc(otherProc);
            const otherPrecElecT = formEPrecursors.reduce((s, prec) => {
              const cInOther = (prec.consumedInProcesses || []).find(
                otherMatchesProc,
              );
              if (!cInOther || !Number(cInOther.amounts)) return s;
              const m =
                otherProd > 0 ? Number(cInOther.amounts) / otherProd : 0;
              return s + m * Number(prec.emissions?.secIndirect || 0);
            }, 0);
            const otherFullElecT = otherProcElecT + otherPrecElecT;
            return {
              direct: acc.direct + (otherSee.see_direct || 0) * massPerT,
              indirect: acc.indirect + (otherSee.see_indirect || 0) * massPerT,
              elec: acc.elec + otherFullElecT * massPerT,
              defaultEm:
                acc.defaultEm + computeDefaultEmFromUpstream(otherProc, qty),
            };
          },
          { direct: 0, indirect: 0, elec: 0, defaultEm: 0 },
        );

        const fullSeeDirect = (see.see_direct || 0) + consumedProcSum.direct;
        const fullSeeIndirect =
          (see.see_indirect || 0) + consumedProcSum.indirect;
        const fullSeeTotal = fullSeeDirect + fullSeeIndirect;
        const embElecPerT =
          procElecPerT + directPrecElecSum + consumedProcSum.elec;

        const directDefaultEm = formEPrecursors.reduce((sum, prec) => {
          if (prec.emissions?.sourceSeeDirect !== "Default") return sum;
          const consumed = (prec.consumedInProcesses || []).find(matchesProc);
          if (!consumed || !Number(consumed.amounts)) return sum;
          return (
            sum +
            Number(prec.emissions?.seeDirect || 0) * Number(consumed.amounts)
          );
        }, 0);
        const totalDefaultEm = directDefaultEm + consumedProcSum.defaultEm;
        const totalEmbDirect = fullSeeDirect * totProd;
        const defaultShare =
          totalEmbDirect > 0 ? totalDefaultEm / totalEmbDirect : 0;

        const cpRow =
          (fF?.carbonRows || []).find(
            (r) => r.processId === (proc.processId || proc.id),
          ) || {};
        const shareF = Number(cpRow.share_of_emissions_covered || 0);

        const cnCodeCompact = getCnCodeForProcess(id);
        const cnCodeDisplay = getDisplayCodeForCompact(cnCodeCompact);

        return {
          //  rowKey MUST match Summary_Products' format so manual data syncs
          rowKey: `proc-${id}`,
          id,
          name,
          cat,
          cnCode: cnCodeDisplay,
          cnName: getDescriptionForCompactCode(cnCodeCompact),
          seeDirect: fullSeeDirect,
          seeIndirect: fullSeeIndirect,
          seeTotal: fullSeeTotal,
          unit: "tCO2e/t",
          defaultShare:
            defaultShare > 0 ? (defaultShare * 100).toFixed(2) + "%" : "0",
          elecSrc: proc.electricityConsumption?.source || "—",
          elecPerT: embElecPerT,
          cpInstrument: fA.carbonPriceInstrument || "—",
          shareEmCovered: fmtN(shareF),
          embEmCovered: fmtN(shareF * fullSeeTotal),
          currency: fF?.currency || "EUR",
          cpDue: fmtN(Number(cpRow.cp_due_per_produced_t_or_MWh) || 0),
          rebateAmt: fmtN(Number(cpRow.rebate_per_produced_t_or_MWh) || 0),
          effectiveCp: fmtN(Number(cpRow.result_effective_cp) || 0),
          isPrecursor: false,
        };
      }),
    [dProcesses, procs, fF, fA, formEPrecursors], // eslint-disable-line
  );

  const [variants, setVariants] = useState({});
  const addVariant = (procId) => {
    setVariants((prev) => ({
      ...prev,
      [procId]: [...(prev[procId] || []), `v-${Date.now()}`],
    }));
  };
  const removeVariant = (procId, variantId) => {
    setVariants((prev) => ({
      ...prev,
      [procId]: (prev[procId] || []).filter((id) => id !== variantId),
    }));
  };

  const expandedRows = useMemo(() => {
    const out = [];
    processRows.forEach((base) => {
      out.push({ ...base, isVariant: false });
      const procVariants = variants[base.id] || [];
      procVariants.forEach((variantId, vi) => {
        out.push({
          ...base,
          //  Same rowKey format as Summary_Products so manual fields sync
          rowKey: `${base.rowKey}__${variantId}`,
          isVariant: true,
          variantId,
          parentProcId: base.id,
          variantNum: vi + 2,
        });
      });
    });
    return out;
  }, [processRows, variants]);

  const precRows = useMemo(() => {
    return buildPrecursorData(allFormData).map((prec) => ({
      rowKey: `prec-${prec.id}`,
      isPrecursor: true,
      id: prec.id,
      name: prec.name || prec.goodsType || "",
      cat: prec.goodsType || prec.name || "",
      cnCode: prec.cnCode,
      cnName: prec.cnName,
      seeDirect: prec.seeDirect,
      seeIndirect: prec.seeIndirect,
      seeTotal: prec.seeTotal,
      unit: "tCO2e/t",
      defaultShare: prec.isDefault ? "TRUE" : "FALSE",
      elecSrc: prec.elecSrc || "",
      elecPerT: prec.elecPerT,
      cpInstrument: prec.cpInstrument || "",
      shareEmCovered: "",
      embEmCovered: "",
      currency: prec.currency || "EUR",
      cpDue: prec.cpDue,
      rebateAmt: prec.rebate,
      effectiveCp: prec.effectiveCp,
    }));
  }, [allFormData]);

  // ── Render one cell ────────────────────────────────────────────────────────
  const cell = (row, n) => {
    const m = getRow(row.rowKey);
    const colDef = COLS[n - 1];

    const man = (placeholder = "—") => (
      <td key={n} className="cyw" style={{ minWidth: colDef.w }}>
        <input
          className="sc-input"
          value={m[n] || ""}
          onChange={(e) => !readOnly && setCell(row.rowKey, n, e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={readOnly}
          style={readOnly ? { cursor: "default", opacity: 0.85 } : undefined}
        />
      </td>
    );
    const auto = (val, extra = "") => (
      <td key={n} className={`cgn${extra}`} style={{ minWidth: colDef.w }}>
        {val}
      </td>
    );
    const id = () => (
      <td
        key={n}
        className="cid"
        style={{ minWidth: colDef.w, textAlign: "left", paddingLeft: 6 }}
      >
        {row.isVariant ? (
          <span style={{ paddingLeft: 10, color: "#5a7a7c" }}>
            <span
              style={{
                fontSize: 9,
                color: "#82a0a3",
                fontWeight: 700,
                marginRight: 4,
              }}
            >
              ↳ #{row.variantNum}
            </span>
            {row.name || row.id}
          </span>
        ) : (
          <>
            <span
              style={{
                fontSize: 9,
                color: "#376366",
                fontWeight: 700,
                marginRight: 4,
                fontFamily: "monospace",
              }}
            >
              {row.id}
            </span>
            {row.name || row.id}
          </>
        )}
      </td>
    );

    switch (n) {
      case 1:
        return id();
      case 2:
        return auto(row.cat);
      //  Col 3 (CN Codes) — read-only, comes from Form A's aggregated goods table only
      case 3:
        return (
          <td key={n} className="cgn rct" style={{ minWidth: colDef.w }}>
            {row.cnCode || (
              <span
                style={{ color: "#999", fontStyle: "italic", fontSize: 9.5 }}
              >
                Not set in Form A
              </span>
            )}
          </td>
        );
      //  Col 4 (CN Name) — read-only, derived from Form A's CN code
      case 4: {
        const cnName = row.cnName || "";

        return (
          <td
            key={n}
            className="cgn cnname-cell"
            style={{
              minWidth: colDef.w,
              maxWidth: colDef.w + 60,
            }}
            title={cnName}
          >
            {cnName || (
              <span
                style={{
                  color: "#999",
                  fontStyle: "italic",
                  fontSize: 9.5,
                }}
              >
                Set a CN code in Form A first
              </span>
            )}
          </td>
        );
      }
      case 5:
        return man();
      case 6:
        return auto(fmtN(row.seeDirect), " rnum");
      case 7:
        return auto(fmtN(row.seeIndirect), " rnum");
      case 8:
        return auto(fmtN(row.seeTotal), " rnum");
      case 9:
        return auto(row.unit, " rct");
      case 10:
        return auto(row.defaultShare, " rct");
      case 11:
        return auto(dash(row.elecSrc), " rct");
      case 12:
        return auto(fmtN(row.elecPerT), " rnum");
      case 13:
      case 14:
      case 15:
      case 16:
      case 17:
      case 18:
      case 19:
      case 20:
      case 21:
      case 22:
      case 23:
      case 24:
      case 25:
      case 26:
      case 27:
      case 28:
      case 29:
      case 30:
      case 31:
      case 32:
      case 33:
      case 34:
        return man();
      case 35:
        return auto(dash(row.cpInstrument), " rct");
      case 36:
        return auto(row.shareEmCovered, " rnum");
      case 37:
        return auto(row.embEmCovered, " rnum");
      case 38:
        return auto(dash(row.currency), " rct");
      case 39:
        return auto(row.cpDue, " rnum");
      case 40:
      case 41:
      case 42:
        return man();
      case 43:
        return auto(row.rebateAmt, " rnum");
      case 44:
        return auto(row.effectiveCp, " rnum");
      default:
        return <td key={n} className="cwh" />;
    }
  };

  const sec1Rows = [
    {
      lbl: "Name of the installation (English name):",
      val: dash(fA.installationNameEnglish || fA.installationName),
      g: aggGoods[0],
      p: procs[0],
      mLbl: "Calculation-based (excl. PFC):",
      mVal: fmtN(calcBased),
      tLbl: "Total direct emissions:",
      tVal: fmtN(totalDirect),
      dq: dqGeneral,
      fLbl: "Carbon price instrument:",
      fVal: dash(fA.carbonPriceInstrument),
    },
    {
      lbl: "Street, Number:",
      val: dash(fA.street),
      g: aggGoods[1],
      p: procs[1],
      mLbl: "Total PFC emissions:",
      mVal: fmtN(pfcTotal),
      tLbl: "Total indirect emissions:",
      tVal: fmtN(totalIndirect),
      dq: dqJustif,
      fLbl: "Any additional information:",
      fVal: dash(fA.additionalInfo),
    },
    {
      lbl: "Economic activity:",
      val: dash(fA.economicActivity),
      g: aggGoods[2],
      p: procs[2],
      mLbl: "Measurement-based:",
      mVal: fmtN(measBased),
      tLbl: "Total emissions:",
      tVal: fmtN(totalAll),
      dq: dqAssur,
      fLbl: null,
      fVal: null,
    },
    {
      lbl: "Country:",
      val: dash(fA.country),
      g: aggGoods[3],
      p: procs[3],
      mLbl: "Other:",
      mVal: fmtN(otherEm),
      tLbl: null,
      tVal: null,
      dq: null,
      fLbl: null,
      fVal: null,
    },
    {
      lbl: "UNLOCODE:",
      val: dash(fA.unLocode),
      g: aggGoods[4],
      p: procs[4],
      mLbl: null,
      mVal: null,
      tLbl: null,
      tVal: null,
      dq: null,
      fLbl: null,
      fVal: null,
    },
    {
      lbl: "Coordinates (latitude):",
      val: dash(fA.latitude),
      g: aggGoods[5],
      p: procs[5],
      mLbl: null,
      mVal: null,
      tLbl: null,
      tVal: null,
      dq: null,
      fLbl: null,
      fVal: null,
    },
    {
      lbl: "Coordinates (longitude):",
      val: dash(fA.longitude),
      g: null,
      p: null,
      mLbl: null,
      mVal: null,
      tLbl: null,
      tVal: null,
      dq: null,
      fLbl: null,
      fVal: null,
    },
    {
      lbl: "Reporting period start:",
      val: fmtDate(fA.reportingPeriodStart),
      g: null,
      p: null,
      mLbl: null,
      mVal: null,
      tLbl: null,
      tVal: null,
      dq: null,
      fLbl: null,
      fVal: null,
    },
    {
      lbl: "Reporting period end:",
      val: fmtDate(fA.reportingPeriodEnd),
      g: null,
      p: null,
      mLbl: null,
      mVal: null,
      tLbl: null,
      tVal: null,
      dq: null,
      fLbl: null,
      fVal: null,
    },
  ];

  return (
    <>
      <style>{CSS}</style>
      <div className="sc">
        <div className="sc-top">
          <span className="sc-top-title">
            Communication with Reporting Declarants
          </span>
          <span className="sc-top-tag">Summary_Communication</span>
          <span className="sc-top-tag">Summary_Processes</span>
          <span className="sc-top-tag">Summary_Products</span>
          <span
            className="sc-top-tag"
            onClick={() => exportSummaryExcel(allFormData, manualData)}
            style={{
              cursor: "pointer",
              backgroundColor: "#C6EFCE",
              color: "#1a5e30",
              fontWeight: 700,
              border: "1px solid #82c891",
            }}
          >
            ⬇ Export to Excel
          </span>
        </div>
        <div className="sc-info">
          This sheet summarises the main information to be communicated to the
          reporting declarants importing the goods into the EU. CN codes come
          from Form A's Goods &amp; Processes tab.
        </div>

        {/* ── Section 1 ── (unchanged) */}
        <div className="sc-sec">
          <div className="sc-band">
            1. Summary of the manufacturing facility and production processes
          </div>
          <div className="sc-scroll">
            <table className="sc-tbl" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th className="csh" colSpan={2}>
                    1. Manufacturing facility details ← Form A
                  </th>
                  <th className="csh" colSpan={8}>
                    2. Production processes &amp; routes ← Form A
                  </th>
                  <th className="csh" colSpan={5}>
                    3. Emissions by monitoring methodology ← Form C
                  </th>
                  <th className="csh" colSpan={2}>
                    4. Further information ← Form A
                  </th>
                </tr>
                <tr>
                  <th className="ch" style={{ minWidth: 240 }}>
                    Parameter
                  </th>
                  <th className="ch" style={{ minWidth: 170 }}>
                    Value
                  </th>
                  <th className="ch" style={{ minWidth: 30 }}>
                    #
                  </th>
                  <th className="ch" style={{ minWidth: 155 }}>
                    (a) Aggregated good produced
                  </th>
                  <th className="ch" style={{ minWidth: 115 }}>
                    Route 1
                  </th>
                  <th className="ch" style={{ minWidth: 115 }}>
                    Route 2
                  </th>
                  <th className="ch" style={{ minWidth: 115 }}>
                    Route 3
                  </th>
                  <th className="ch" style={{ minWidth: 30 }}>
                    #
                  </th>
                  <th className="ch" style={{ minWidth: 175 }}>
                    (b) Production process
                  </th>
                  <th className="ch" style={{ minWidth: 125 }}>
                    Included goods (1–3)
                  </th>
                  <th className="ch" style={{ minWidth: 210 }}>
                    Methodology
                  </th>
                  <th className="ch" style={{ minWidth: 110 }}>
                    tCO2e
                  </th>
                  <th className="ch" style={{ minWidth: 200 }}>
                    Total
                  </th>
                  <th className="ch" style={{ minWidth: 110 }}>
                    tCO2e
                  </th>
                  <th className="ch" style={{ minWidth: 155 }}>
                    Data quality
                  </th>
                  <th className="ch" style={{ minWidth: 180 }}>
                    Parameter
                  </th>
                  <th className="ch" style={{ minWidth: 160 }}>
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {sec1Rows.map((r, i) => (
                  <tr key={i}>
                    <td className="clb">{r.lbl}</td>
                    <td className="cgn">{r.val}</td>
                    <td className="cid">{r.g?.id || "—"}</td>
                    <td className="cgn">{dash(r.g?.category)}</td>
                    <td className="cgn">
                      {dash(r.g?.primaryRoute || r.g?.route1)}
                    </td>
                    <td className="cgn">{dash(r.g?.route2)}</td>
                    <td className="cgn">{dash(r.g?.route3)}</td>
                    <td className="cid">{r.p?.id || "—"}</td>
                    <td className="cgn">
                      {r.p ? (
                        <>
                          <div style={{ fontWeight: 600 }}>
                            {dash(r.p.name)}
                          </div>
                          <div style={{ fontSize: 9, color: "#666" }}>
                            {dash(r.p.aggregatedGoodsCategory)}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="cgn" style={{ fontSize: 10 }}>
                      {r.p
                        ? [r.p.inc1, r.p.inc2, r.p.inc3]
                            .filter(Boolean)
                            .join(", ") || "—"
                        : "—"}
                    </td>
                    <td className={r.mLbl ? "clb" : "cwh"}>{r.mLbl || ""}</td>
                    <td
                      className={r.mVal && r.mVal !== "—" ? "cgn rnum" : "cwh"}
                    >
                      {r.mVal || ""}
                    </td>
                    <td
                      className={
                        r.tLbl
                          ? r.tLbl.includes("Total emissions:")
                            ? "ctot"
                            : "clb"
                          : "cwh"
                      }
                    >
                      {r.tLbl || ""}
                    </td>
                    <td
                      className={
                        r.tVal && r.tVal !== "—"
                          ? r.tLbl?.includes("Total emissions:")
                            ? "ctot rnum"
                            : "cgn rnum"
                          : "cwh"
                      }
                    >
                      {r.tVal || ""}
                    </td>
                    <td className={r.dq ? "cgn" : "cwh"}>{r.dq || ""}</td>
                    <td className={r.fLbl ? "clb" : "cwh"}>{r.fLbl || ""}</td>
                    <td className={r.fVal ? "cgn" : "cwh"}>{r.fVal || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Section 2 — all 44 columns ── */}
        <div className="sc-sec">
          <div className="sc-band">2. Summary of products</div>
          <div className="sc-scroll">
            <table className="sc-tbl">
              <thead>
                <tr>
                  <th className="csh" colSpan={5}>
                    Product identification
                  </th>
                  <th className="csh" colSpan={7}>
                    Specific Embedded Emissions &amp; Electricity ← Form D / E
                  </th>
                  <th className="csh" colSpan={22}>
                    Additional product parameters (manual entry)
                  </th>
                  <th className="csh" colSpan={10}>
                    Carbon pricing ← Form A / F
                  </th>
                </tr>
                <tr>
                  {COLS.map((c) => (
                    <th
                      key={c.n}
                      className="ch"
                      style={{
                        minWidth: c.w,
                        maxWidth: c.w + 20,
                        fontSize: 9,
                        whiteSpace: "pre-wrap",
                        verticalAlign: "bottom",
                        lineHeight: 1.3,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 8,
                          opacity: 0.6,
                          display: "block",
                          marginBottom: 1,
                        }}
                      >
                        [{c.n}]
                      </span>
                      {c.label}
                      {c.type === "manual" && (
                        <span
                          style={{
                            display: "block",
                            fontSize: 7.5,
                            opacity: 0.75,
                            marginTop: 2,
                          }}
                        >
                          ✏ manual
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expandedRows.length > 0 && (
                  <tr className="sc-divider-row">
                    <td colSpan={44}>
                      Production processes ← Form D &nbsp;&nbsp; (
                      {processRows.length} process
                      {processRows.length !== 1 ? "es" : ""},{" "}
                      {expandedRows.length} product
                      {expandedRows.length !== 1 ? "s" : ""})
                    </td>
                  </tr>
                )}
                {expandedRows.map((row, idx) => {
                  const next = expandedRows[idx + 1];
                  const isLastOfProcess = !next || next.id !== row.id;
                  return (
                    <React.Fragment key={row.rowKey}>
                      <tr
                        style={row.isVariant ? { background: "#fafdf9" } : {}}
                      >
                        {COLS.map((c) => cell(row, c.n))}
                      </tr>
                      {isLastOfProcess && !readOnly && (
                        <tr>
                          <td
                            colSpan={44}
                            style={{
                              background: "#f6f9f8",
                              padding: "3px 8px",
                              border: "1px solid #c0c0c0",
                              fontSize: 10,
                            }}
                          >
                            <button
                              onClick={() =>
                                addVariant(row.parentProcId || row.id)
                              }
                              style={{
                                background: "#dbeed8",
                                border: "1px solid #82c891",
                                color: "#1a5e30",
                                fontSize: 10,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 3,
                                cursor: "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              + Add product variant for {row.name || row.id}
                            </button>
                            {row.isVariant && (
                              <button
                                onClick={() =>
                                  removeVariant(row.parentProcId, row.variantId)
                                }
                                style={{
                                  background: "transparent",
                                  border: "1px solid #d99",
                                  color: "#a33",
                                  fontSize: 10,
                                  padding: "2px 8px",
                                  borderRadius: 3,
                                  cursor: "pointer",
                                  fontFamily: "inherit",
                                  marginLeft: 8,
                                }}
                              >
                                Remove last variant
                              </button>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {precRows.length > 0 && (
                  <tr className="sc-divider-row">
                    <td colSpan={44}>
                      Purchased precursors ← Form A / E ({precRows.length} row{precRows.length !== 1 ? "s" : ""})
                    </td>
                  </tr>
                )}
                {precRows.map((row) => (
                  <tr key={row.rowKey}>
                    {COLS.map(c => cell(row, c.n))}
                  </tr>
                ))}

                {expandedRows.length === 0 && precRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={44}
                      style={{
                        textAlign: "center",
                        color: "#888",
                        padding: 14,
                        background: "#fff",
                      }}
                    >
                      No data yet — complete Forms A–F to populate this table.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="sc-legend">
              <span>
                <b style={{ background: "#CCFFCC" }} />
                <span>
                  Auto-filled from forms (cols 1,2,4,6–12,35–39,43,44)
                </span>
              </span>
              <span>
                <b style={{ background: "#FFFF99" }} />
                <span>
                  Manual entry — product name (col 5), material composition
                  (cols 13–34), rebate (cols 40–42)
                </span>
              </span>
              <span>
                <b style={{ background: "#E5F0F1" }} />
                <span>ID (auto)</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SummaryCommunication;
